/*
 * st-dc.js - the minimal runtime that lets a Seedtime design-canvas artboard
 * run as a Shopify section.
 *
 * The marketing pages are authored in Claude Design as .dc.html artboards. Each
 * one carries a `class Component extends DCLogic` script whose renderVals()
 * returns the values its markup binds to. Rather than hand-translating that
 * logic into vanilla JS once per page - sixteen different components, from a
 * pricing toggle to a nine-crop harvest wall - this file reimplements just
 * enough of the runtime for the original scripts to run unchanged. Porting a
 * page is then a mechanical copy of its markup plus its script.
 *
 * Supported, because that is all the artboards use:
 *   [[ dotted.path ]]          in text nodes and in attribute values
 *                              (the canvas writes {{ }}; gen/port_page.py rewrites
 *                              them, because {{ }} is Liquid's own output tag)
 *   <sc-if value="[[ x ]]">    keeps its children when x is truthy
 *   <sc-for list="[[ xs ]]" as="item">  repeats, with [[ item.y ]] and [[ $index ]]
 *   onClick="[[ handler ]]"    ported to data-st-on="handler" (see gen/port_page.py)
 *
 * Re-render replaces the root's children wholesale. That is fine here: these
 * are display components with no text inputs and no focus to preserve.
 */
(function () {
  'use strict';

  function lookup(path, scope, vals) {
    if (path === 'true') return true;
    if (path === 'false') return false;
    var parts = path.split('.');
    var cur = Object.prototype.hasOwnProperty.call(scope, parts[0]) ? scope : vals;
    for (var i = 0; i < parts.length; i++) {
      if (cur === null || cur === undefined) return undefined;
      cur = cur[parts[i]];
    }
    return cur;
  }

  var HOLE = /\[\[\s*([\w$.]+)\s*\]\]/g;

  function fill(str, scope, vals) {
    return str.replace(HOLE, function (_, path) {
      var v = lookup(path, scope, vals);
      return v === undefined || v === null || typeof v === 'function' ? '' : String(v);
    });
  }

  // Resolve one template node into zero or more real nodes.
  function resolve(node, scope, vals, out) {
    if (node.nodeType === 3) {                       // text
      var txt = node.nodeValue;
      out.push(document.createTextNode(HOLE.test(txt) ? fill(txt, scope, vals) : txt));
      HOLE.lastIndex = 0;
      return;
    }
    if (node.nodeType !== 1) return;                 // drop comments

    var tag = node.localName;

    if (tag === 'sc-if') {
      var raw = node.getAttribute('value') || '';
      var m = raw.match(/\[\[\s*([\w$.]+)\s*\]\]/);
      if (m && lookup(m[1], scope, vals)) children(node, scope, vals, out);
      return;
    }

    if (tag === 'sc-for') {
      var lm = (node.getAttribute('list') || '').match(/\[\[\s*([\w$.]+)\s*\]\]/);
      var as = node.getAttribute('as') || 'item';
      var list = lm ? lookup(lm[1], scope, vals) : null;
      if (Array.isArray(list)) {
        list.forEach(function (item, i) {
          var inner = Object.create(scope);
          inner[as] = item;
          inner.$index = i;
          children(node, inner, vals, out);
        });
      }
      return;
    }

    // localName, not tagName: tagName is uppercased for HTML elements, and
    // createElementNS('...xhtml', 'IMG') builds an unknown element that renders
    // nothing. That silently blanked every image on the first port.
    var el = document.createElementNS(node.namespaceURI, node.localName);
    for (var a = 0; a < node.attributes.length; a++) {
      var at = node.attributes[a];
      if (at.name === 'data-st-on') {
        var fn = lookup(fill(at.value, scope, vals) || at.value, scope, vals);
        if (typeof fn === 'function') el.__stOn = fn;
        continue;
      }
      el.setAttribute(at.name, fill(at.value, scope, vals));
    }
    children(node, scope, vals, el);
    out.push ? out.push(el) : out.appendChild(el);
  }

  function children(node, scope, vals, out) {
    var kids = [];
    for (var i = 0; i < node.childNodes.length; i++) resolve(node.childNodes[i], scope, vals, kids);
    if (out.push) kids.forEach(function (k) { out.push(k); });
    else kids.forEach(function (k) { out.appendChild(k); });
  }

  // A render replaces the root's children wholesale, which throws away anything
  // a third-party script mounted inside them. The TrustBox widgets are exactly
  // that: tp.widget.bootstrap.min.js loads async and swaps each
  // .trustpilot-widget div's contents for an iframe. Two ways that breaks -
  // the bootstrap can win the race against first render and have its iframe
  // wiped, and on the home page every hero-carousel tab click re-renders and
  // wipes it again. Re-mounting after each render covers both. If the bootstrap
  // has not arrived yet there is nothing to do: its own initial scan finds the
  // freshly rendered divs.
  // Trustpilot swaps each .trustpilot-widget div's contents for an iframe once
  // it mounts. A mounted one looks like this:
  function isMounted(el) { return !!el.querySelector('iframe'); }

  // Bring an existing element up to date with its freshly rendered twin without
  // replacing it, so the node - and anything a third party mounted inside it -
  // stays exactly where it is.
  function syncAttrs(oldEl, newEl) {
    for (var i = oldEl.attributes.length - 1; i >= 0; i--) {
      var a = oldEl.attributes[i];
      if (!newEl.hasAttribute(a.name)) oldEl.removeAttribute(a.name);
    }
    for (var j = 0; j < newEl.attributes.length; j++) {
      var b = newEl.attributes[j];
      if (oldEl.getAttribute(b.name) !== b.value) oldEl.setAttribute(b.name, b.value);
    }
    // handlers come from the fresh render and close over the current state
    oldEl.__stOn = newEl.__stOn;
  }

  // Update the live tree in place instead of rebuilding it.
  //
  // Wiping the root and re-appending was cheap and correct for markup, but it
  // DETACHES every node - and re-attaching an iframe makes the browser reload
  // it. That is why the TrustBox flashed on every hero-tab click even once the
  // node itself was being carried across: same element, but re-parented.
  // Keeping nodes in place fixes it at the source, and stops images reloading
  // on every tab switch as a side effect.
  function patch(parent, next) {
    var oldKids = [], newKids = [], i;
    for (i = 0; i < parent.childNodes.length; i++) oldKids.push(parent.childNodes[i]);
    for (i = 0; i < next.childNodes.length; i++) newKids.push(next.childNodes[i]);
    for (i = 0; i < Math.max(oldKids.length, newKids.length); i++) {
      var o = oldKids[i], n = newKids[i];
      if (!n) { parent.removeChild(o); continue; }
      if (!o) { parent.appendChild(n); continue; }
      if (o.nodeType !== n.nodeType || o.nodeName !== n.nodeName) {
        parent.replaceChild(n, o);
        continue;
      }
      if (o.nodeType === 3) {
        if (o.nodeValue !== n.nodeValue) o.nodeValue = n.nodeValue;
        continue;
      }
      if (o.nodeType !== 1) continue;
      syncAttrs(o, n);
      // never reach inside a third-party widget - its contents are not ours
      if (o.className && String(o.className).indexOf('trustpilot-widget') >= 0) continue;
      patch(o, n);
    }
  }

  // Only ever mounts what is not mounted yet: the first render, and any widget
  // the carry-over could not match up.
  function remountTrustBoxes(root) {
    var tp = window.Trustpilot;
    if (!tp || typeof tp.loadFromElement !== 'function') return;
    var boxes = root.querySelectorAll('.trustpilot-widget');
    for (var i = 0; i < boxes.length; i++) {
      if (isMounted(boxes[i])) continue;
      try { tp.loadFromElement(boxes[i], true); } catch (e) {}
    }
  }

  function DCLogic(props) {
    this.props = props || {};
    this.state = null;
  }
  DCLogic.prototype.setState = function (patch) {
    this.state = Object.assign({}, this.state, patch);
    if (this.__render) this.__render();
  };
  DCLogic.prototype.forceUpdate = function () { if (this.__render) this.__render(); };
  window.DCLogic = DCLogic;

  function boot(root) {
    if (root.__stBooted) return;
    root.__stBooted = true;

    var src = root.querySelector('script[type="text/st-logic"]');
    if (!src) return;
    // data-st-props carries the artboard's PROP SCHEMA, not its values: each
    // entry looks like {editor, options, default, section}. The real runtime
    // hands the component the resolved value, so unwrap `default` here.
    // Passing the schema object straight through makes every
    // `this.props.x === 'something'` test fail, which silently collapses each
    // sc-if branch and renders the toggled screenshots not at all.
    var props = {};
    try {
      var schema = JSON.parse(root.getAttribute('data-st-props') || '{}');
      for (var key in schema) {
        var entry = schema[key];
        props[key] = (entry && typeof entry === 'object' && 'default' in entry)
          ? entry['default'] : entry;
      }
    } catch (e) {}

    var Component;
    try {
      Component = new Function('DCLogic', src.textContent + '\nreturn Component;')(DCLogic);
    } catch (e) {
      // A broken logic script must not take the page's copy down with it: leave
      // the server-rendered fallback in place and say so in the console.
      if (window.console) console.error('[st-dc] logic script failed to compile', e);
      // The shell hides sc-if / sc-for so their raw contents cannot flash before
      // this runs. If the logic never compiles that hiding would swallow the
      // fallback too, so hand it back.
      root.setAttribute('data-st-failed', '');
      return;
    }
    src.parentNode.removeChild(src);

    var tpl = document.createElement('template');
    tpl.innerHTML = root.innerHTML;

    var inst = new Component(props);
    inst.__render = function () {
      var frag = document.createDocumentFragment();
      children(tpl.content, {}, inst.renderVals(), frag);
      if (root.__stPainted) {
        patch(root, frag);
      } else {
        root.textContent = '';
        root.appendChild(frag);
        root.__stPainted = true;
      }
      remountTrustBoxes(root);
    };

    root.addEventListener('click', function (ev) {
      for (var n = ev.target; n && n !== root; n = n.parentNode) {
        if (n.__stOn) { ev.preventDefault(); n.__stOn(ev); return; }
      }
    });

    inst.__render();
    if (typeof inst.componentDidMount === 'function') inst.componentDidMount();
  }

  // Landing on /#toolshed from another page went wrong two ways. The browser
  // scrolls to the fragment while parsing, but every [data-st-root] then
  // rebuilds its children - so the element the browser measured is gone and the
  // page settles in the wrong place (measured 149px off on the home page). And
  // theme.css sets html { scroll-behavior: smooth }, so the arrival animated
  // the whole way down from the top instead of just being there.
  //
  // Re-aim once the DOM is final, with smooth suppressed for that one jump.
  // scrollIntoView honours the target's scroll-margin-top, so it still lands
  // flush under the sticky nav.
  function jumpToHash() {
    if (!location.hash || location.hash.length < 2) return;
    var el;
    try { el = document.querySelector(location.hash); } catch (e) { return; }
    if (!el) return;
    var root = document.documentElement;
    var had = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    el.scrollIntoView();
    root.style.scrollBehavior = had;
  }

  function start() {
    var roots = document.querySelectorAll('[data-st-root]');
    for (var i = 0; i < roots.length; i++) boot(roots[i]);
  }

  // One jump is not enough: the webfont swapping in and late images landing both
  // change the page's height AFTER the aim, which slides the target back out of
  // place. Re-aim each frame until the height stops moving - and give up the
  // moment the reader scrolls, so this never fights them for the viewport.
  function aimUntilSettled() {
    if (!location.hash || location.hash.length < 2) return;
    var last = -1, frames = 0, stop = false;
    function release() { stop = true; }
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (n) {
      addEventListener(n, release, { once: true, passive: true });
    });
    (function tick() {
      if (stop) return;
      var h = document.documentElement.scrollHeight;
      jumpToHash();
      if (h !== last && frames++ < 30) {
        last = h;
        requestAnimationFrame(tick);
      } else {
        ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (n) {
          removeEventListener(n, release);
        });
      }
    })();
  }

  function startAndAim() {
    start();
    aimUntilSettled();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startAndAim);
  } else {
    startAndAim();
  }
  if (document.readyState !== 'complete') addEventListener('load', aimUntilSettled);
  // Shopify's theme editor re-injects sections without a page load.
  document.addEventListener('shopify:section:load', start);
})();
